// ------ Types for Params ------
export interface GetAllOfficesParams {
  skip?: number;
  take?: number;
  search?: string;
  status?: 'ACTIVE' | 'INACTIVE';
  type?: 'HEAD' | 'BRANCH';
}

export interface CreateOfficeParams {
  name: string;
  type: 'HEAD' | 'BRANCH';
  parent_office_id?: string;
  location: string;
  geo_fence: boolean;
  status: 'ACTIVE' | 'INACTIVE';
  address: string;
  timezone: string;
  working_days: string[];
  latitude: string;
  longitude: string;
  radius: number;
  outside_fence_action: 'ALLOW_ATTENDANCE' | 'BLOCK_ATTENDANCE';
  default_shift_start_time: string;
  default_shift_end_time: string;
  default_shift_check_in_grace_period: number;
}

export interface UpdateOfficeParams extends Partial<CreateOfficeParams> {
  id: string;
}

export interface UpdateCompanyParams {
  company_name?: string;
  company_email?: string;
  admin_name?: string;
  admin_email?: string;
  admin_phone?: string;
  contact_person_name?: string;
  contact_person_email?: string;
  contact_person_phone?: string;
  country?: string;
  timezone?: string;
  working_days?: string[];
  latitude?: string;
  longitude?: string;
  radius?: number;
  outside_fence_action?: 'ALLOW_ATTENDANCE' | 'BLOCK_ATTENDANCE';
  default_shift_start_time?: string;
  default_shift_end_time?: string;
  default_shift_check_in_grace_period?: number;
  logo_url?: string;
}

export interface CompanyDetailsData {
  organization_id: string;
  total_employee_count:number;
  company_name: string;
  company_email: string;
  admin_name: string;
  admin_email: string;
  admin_phone: string;
  contact_person_name: string;
  contact_person_email: string;
  contact_person_phone: string;
  country: string;
  timezone: string;
  main_office_id: string;
  working_days: string[];
  latitude: string;
  longitude: string;
  radius: number;
  outside_fence_action: 'ALLOW_ATTENDANCE' | 'BLOCK_ATTENDANCE';
  default_shift_start_time: string;
  default_shift_end_time: string;
  default_shift_check_in_grace_period: number;
  logo_url?: string;
}

export interface CompanyDetailsResponse {
  success: boolean;
  message: string | null;
  data: CompanyDetailsData;
}

// ------ Types for Data ------
export interface Office {
  id: string;
  organization_id: string;
  tenant_id: string;
  name: string;
  type: 'HEAD' | 'BRANCH';
  parent_office_id?: string;
  location: string;
  geo_fence: boolean;
  status: 'ACTIVE' | 'INACTIVE';
  address: string;
  timezone: string;
  working_days: string[];
  latitude: string;
  longitude: string;
  radius: number;
  outside_fence_action: 'ALLOW_ATTENDANCE' | 'BLOCK_ATTENDANCE';
  default_shift_start_time: string;
  default_shift_end_time: string;
  default_shift_check_in_grace_period: number;
  created_at: string;
  updated_at: string;
  parent_office?: {
    id: string;
    name: string;
  };
  child_offices?: {
    id: string;
    name: string;
  }[];
}

// ------ Types for Response ------
export interface AllOfficesResponse {
  success: boolean;
  message: string;
  data: {
    data: Office[];
    meta: {
      total: number;
      skip: number;
      take: number;
    };
  };
}

export interface OfficeResponse {
  success: boolean;
  message: string;
  data: Office;
}
// Failed Offices types
export interface FailedOfficeRecord {
  id: string;
  row_number: number;
  office_name: string;
  raw_data: {
    name: string;
    type: string;
    radius: number;
    status: string;
    latitude: string;
    timezone: string;
    geo_fence: boolean;
    longitude: string;
    working_days: string[];
    outside_fence_action: string;
    default_shift_end_time: string;
    default_shift_start_time: string;
    default_shift_check_in_grace_period: number;
  };
  failure_reason: string;
  created_at: string;
}

export type FailedOfficeListResponse = {
  data: FailedOfficeRecord[];
  meta: {
    total: number;
    page: number;
    limit: number;
    last_page: number;
  };
};

export type FailedOfficeListParams = {
  page: number;
  limit: number;
  job_id?: string;
};


// ---------------------------------------------------------------------------
// JTI: company-defined extra employee fields
// ---------------------------------------------------------------------------

/** How a field is filled in. Mirrors the API's `field_type` enum. */
export type CustomFieldType = 'INPUT' | 'DATE' | 'SWITCH' | 'SELECT';

export const CUSTOM_FIELD_TYPES: { label: string; value: CustomFieldType }[] = [
  { label: 'Input', value: 'INPUT' },
  { label: 'Date', value: 'DATE' },
  { label: 'Switch', value: 'SWITCH' },
  { label: 'Select', value: 'SELECT' },
];

export interface CustomEmployeeField {
  id: string;
  organization_id?: string;
  tenant_id?: string;
  /** The label, and also the key an employee's value is stored under. */
  field_name: string;
  field_type: CustomFieldType;
  /** Only for SELECT. */
  options?: string[] | null;
  status: 'active' | 'inactive';
  is_required: boolean;
  /** Ascending; ties fall back to creation order. */
  sort_order: number;
  created_at?: string;
  updated_at?: string;
}

export interface CustomEmployeeFieldParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
}

export interface CustomEmployeeFieldListResponse {
  success: boolean;
  message: string | null;
  data: {
    data: CustomEmployeeField[];
    meta: { total: number; page: number; limit: number; totalPages?: number };
  };
}

/** `/active` answers with a flat list, not a paginated one. */
export interface ActiveCustomFieldsResponse {
  success: boolean;
  message: string | null;
  data: CustomEmployeeField[];
}

export interface UpsertCustomFieldBody {
  /** Present when editing. */
  id?: string;
  field_name: string;
  field_type: CustomFieldType;
  options?: string[];
  status?: 'active' | 'inactive';
  is_required?: boolean;
  sort_order?: number;
}
